import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ApiError, NetworkError, TimeoutError } from './ApiError';
import { getApiBaseUrl, API_CONFIG } from './config';
import { notificationService } from '@/infrastructure/notifications';
import { getErrorMessage } from './errorMessages';
import { useAuthStore } from '../../application/store/authStore';

/**
 * Extended Axios config with custom options for error handling
 */
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  /**
   * Whether to show error toast notification (default: true)
   * Set to false to handle errors manually
   */
  showErrorToast?: boolean;
}

/**
 * ApiClient - Type-safe HTTP client wrapper around Axios
 *
 * Features:
 * - Automatic JWT token attachment
 * - Request/response interceptors
 * - Automatic token refresh on 401
 * - Retry logic for transient failures
 * - Typed error handling
 * - Automatic error toast notifications (can be disabled per request)
 */
export class ApiClient {
  private readonly client: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor(baseURL?: string) {
    this.client = axios.create({
      baseURL: baseURL ?? getApiBaseUrl(),
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true // Important: send cookies for httpOnly token strategy
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - attach auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken();
        if (token !== null && typeof config.headers !== 'undefined') {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: unknown) => {
        return Promise.reject(this.handleError(error, undefined));
      }
    );

    // Response interceptor - handle errors and token refresh
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

        // Handle 401 Unauthorized - try to refresh token
        if (error.response?.status === 401 && originalRequest && !this.isRefreshTokenRequest(originalRequest)) {
          try {
            // Prevent multiple simultaneous refresh requests
            if (!this.refreshTokenPromise) {
              this.refreshTokenPromise = this.refreshAccessToken();
            }

            const newToken = await this.refreshTokenPromise;
            this.refreshTokenPromise = null;

            // Retry original request with new token
            if (typeof originalRequest.headers !== 'undefined') {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.client(originalRequest);
          } catch (refreshError) {
            // Token refresh failed - user needs to log in again
            this.clearStoredToken();
            this.refreshTokenPromise = null;

            // Don't show error toast for failed refresh - user will be redirected
            const refreshConfig: ExtendedAxiosRequestConfig = { showErrorToast: false };

            // Redirect to login page
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }

            return Promise.reject(this.handleError(refreshError, refreshConfig));
          }
        }

        return Promise.reject(this.handleError(error, originalRequest));
      }
    );
  }

  /**
   * Check if this is a refresh token request
   */
  private isRefreshTokenRequest(config: AxiosRequestConfig): boolean {
    return config.url?.includes('/auth/refresh') ?? false;
  }

  /**
   * Refresh the access token
   * Backend reads refresh token from httpOnly cookie (sent automatically with withCredentials)
   */
  private async refreshAccessToken(): Promise<string> {
    try {
      const currentToken = this.getStoredToken() || '';

      const response = await this.client.post<{
        succeeded: boolean;
        accessToken: string;
        tokenExpiryTime: string;
      }>('/api/auth/refresh', {
        accessToken: currentToken,
        refreshToken: ''
      });

      if (!response.data.succeeded) {
        throw new ApiError('Token refresh failed', 401);
      }

      const newToken = response.data.accessToken;
      this.setStoredToken(newToken);

      // Sync token expiry back to auth store so proactive timer stays accurate
      useAuthStore.setState({
        tokenExpiresAt: response.data.tokenExpiryTime,
        sessionWarningShown: false
      });

      return newToken;
    } catch (error) {
      throw new ApiError('Failed to refresh token', 401);
    }
  }

  /**
   * Get stored auth token
   */
  private getStoredToken(): string | null {
    if (typeof window === 'undefined') {return null;}
    return localStorage.getItem('authToken');
  }

  /**
   * Store auth token
   */
  private setStoredToken(token: string): void {
    if (typeof window === 'undefined') {return;}
    localStorage.setItem('authToken', token);
  }

  /**
   * Clear stored auth token
   */
  private clearStoredToken(): void {
    if (typeof window === 'undefined') {return;}
    localStorage.removeItem('authToken');
  }

  /**
   * Show error notification toast
   */
  private showErrorNotification(error: Error, config?: ExtendedAxiosRequestConfig): void {
    // Check if error toasts are enabled for this request (default: true)
    const showToast = config?.showErrorToast ?? true;
    if (!showToast) {
      return;
    }

    // Don't show toast for 401 errors during token refresh (user will be redirected)
    if (ApiError.isApiError(error) && error.statusCode === 401) {
      // Only show 401 errors if it's not during token refresh
      const isRefreshRequest = this.isRefreshTokenRequest(config || {});
      if (isRefreshRequest) {
        return;
      }
    }

    // Get user-friendly error message
    const userMessage = getErrorMessage(error);

    // Show error notification
    notificationService.showError(userMessage);
  }

  /**
   * Handle errors and convert to typed ApiError
   */
  private handleError(error: unknown, config?: ExtendedAxiosRequestConfig): Error {
    let typedError: Error;

    if (axios.isAxiosError(error)) {
      // Backend may return errors in different formats:
      // 1. { message: string, errors?: Record<string, string[]> } - standard format
      // 2. { errors: string[] } - authentication endpoints (flat array)
      const axiosError = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]> | string[];
        traceId?: string;
      }>;

      // Network error (no response)
      if (!axiosError.response) {
        if (axiosError.code === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
          typedError = new TimeoutError();
        } else {
          typedError = new NetworkError();
        }
      } else {
        // API error (with response)
        const { status, data } = axiosError.response;
        let message = data?.message ?? axiosError.message;
        let errors: Record<string, string[]> | undefined;
        const traceId = data?.traceId;

        // Handle errors field - may be flat array or dictionary
        if (data?.errors) {
          if (Array.isArray(data.errors)) {
            // Flat array format from auth endpoints: { errors: ["message1", "message2"] }
            // Use first error as the message if no message provided
            const flatErrors = data.errors as string[];
            if (flatErrors.length > 0) {
              const firstError = flatErrors[0];
              if (firstError !== undefined && message === axiosError.message) {
                // Replace generic axios message with actual error
                message = firstError;
              }
            }
            // Convert to dictionary format for ApiError
            errors = { general: flatErrors };
          } else {
            // Dictionary format: { errors: { field: ["error1", "error2"] } }
            errors = data.errors as Record<string, string[]>;
          }
        }

        typedError = new ApiError(message, status, errors, traceId);
      }
    } else if (error instanceof Error) {
      typedError = error;
    } else {
      typedError = new Error('An unknown error occurred');
    }

    // Show error notification
    this.showErrorNotification(typedError, config);

    return typedError;
  }

  /**
   * Retry logic for transient failures
   */
  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    retries: number = API_CONFIG.MAX_RETRIES
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      if (retries > 0 && this.shouldRetry(error)) {
        // Exponential backoff
        const delay = API_CONFIG.RETRY_DELAY * (API_CONFIG.MAX_RETRIES - retries + 1);
        await new Promise((resolve) => setTimeout(resolve, delay));

        return this.retryRequest(requestFn, retries - 1);
      }

      throw error;
    }
  }

  /**
   * Check if error should trigger a retry
   */
  private shouldRetry(error: unknown): boolean {
    if (ApiError.isApiError(error)) {
      const retryableCodes: number[] = [...API_CONFIG.RETRY_STATUS_CODES];
      return retryableCodes.includes(error.statusCode);
    }

    if (NetworkError.isNetworkError(error) || TimeoutError.isTimeoutError(error)) {
      return true;
    }

    return false;
  }

  /**
   * HTTP GET request
   *
   * @param url - The endpoint URL
   * @param config - Request configuration (optional)
   * @param config.showErrorToast - Whether to show error toast notification (default: true)
   */
  async get<T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.get<T>(url, config);
      return response.data;
    });
  }

  /**
   * HTTP POST request
   *
   * @param url - The endpoint URL
   * @param data - Request body data
   * @param config - Request configuration (optional)
   * @param config.showErrorToast - Whether to show error toast notification (default: true)
   */
  async post<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * HTTP PUT request
   *
   * @param url - The endpoint URL
   * @param data - Request body data
   * @param config - Request configuration (optional)
   * @param config.showErrorToast - Whether to show error toast notification (default: true)
   */
  async put<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  /**
   * HTTP PATCH request
   *
   * @param url - The endpoint URL
   * @param data - Request body data
   * @param config - Request configuration (optional)
   * @param config.showErrorToast - Whether to show error toast notification (default: true)
   */
  async patch<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * HTTP DELETE request
   *
   * @param url - The endpoint URL
   * @param config - Request configuration (optional)
   * @param config.showErrorToast - Whether to show error toast notification (default: true)
   */
  async delete<T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Set auth token (called after login)
   */
  setAuthToken(token: string): void {
    this.setStoredToken(token);
  }

  /**
   * Clear auth token (called on logout)
   */
  clearAuthToken(): void {
    this.clearStoredToken();
  }
}

/**
 * Default API client instance
 */
export const apiClient = new ApiClient();
